/**
 * Transpose raw inflation rows into the per-percentile arrays Plotly wants.
 *
 * Wire keys are `obs_date`, `p5/p16/p50/p84/p95`, `actual`, `"one month
 * ago"` (literal string with spaces — verified against the live
 * `/api/economic-outlook/inflation` response), and `cb` for central-bank dots. The
 * long-form `concept/val` shape is also accepted for older payloads.
 */
import type { InflationRow, ProcessedInflation } from '../types';

export function processInflationData(rows: InflationRow[]): ProcessedInflation {
  const dateData: Record<string, Record<string, number | null | undefined>> = {};

  for (const row of rows) {
    const dateStr = row.obs_date ?? row.date ?? row.Date;
    if (!dateStr) continue;

    const bucket = dateData[dateStr] ?? (dateData[dateStr] = {});

    if (row.concept && row.val !== undefined) {
      bucket[row.concept] = row.val ?? null;
      continue;
    }

    for (const col of ['p5', 'p16', 'p50', 'p84', 'p95', 'actual', 'cb'] as const) {
      const value = row[col];
      if (value !== undefined) {
        bucket[col] = value ?? null;
      }
    }
    // Wire emits a literal "one month ago" key (with spaces). Map to the
    // safer `one_month` array key on the processed result.
    const prevMonth = row['one month ago'];
    if (prevMonth !== undefined) {
      bucket['one month ago'] = prevMonth ?? null;
    }
  }

  const dates = Object.keys(dateData).sort();
  const result: ProcessedInflation = {
    dates,
    p5: [],
    p16: [],
    p50: [],
    p84: [],
    p95: [],
    actual: [],
    one_month: [],
    cb: [],
  };

  for (const date of dates) {
    const day = dateData[date];
    result.p5.push(numberOrNull(day?.p5));
    result.p16.push(numberOrNull(day?.p16));
    result.p50.push(numberOrNull(day?.p50));
    result.p84.push(numberOrNull(day?.p84 ?? day?.p16));
    result.p95.push(numberOrNull(day?.p95));
    result.actual.push(numberOrNull(day?.actual));
    result.one_month.push(numberOrNull(day?.['one month ago']));
    result.cb.push(numberOrNull(day?.cb));
  }

  return result;
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : null;
}
